import { Controller, type Control } from 'react-hook-form';
import { Chip, Field, FieldGrid, InfoTip, TextArea, TextInput } from '@zarya/ui';
import { grantsText, conditionsText, type PolicyFormValues, type A2ARef } from './PolicyModel';
import { t } from '@app/i18n';
import s from './Policies.module.css';

export interface PolicyGeneralTabProps {
  control: Control<PolicyFormValues>;
  valores: PolicyFormValues;
  /** Identidades selecionadas, já resolvidas em slug para o resumo. */
  selecionadas: A2ARef[];
  editable: boolean;
}

/**
 * Aba **Geral** do drawer de política (T12).
 *
 * O resumo ao vivo (FR-080) fica aqui de propósito: quem escolhe o modo e o
 * estado precisa ver, na mesma tela, o que a política concede hoje.
 */
export function PolicyGeneralTab({
  control,
  valores,
  selecionadas,
  editable,
}: PolicyGeneralTabProps) {
  return (
    <>
      <FieldGrid>
        <Controller
          control={control}
          name="name"
          render={({ field, fieldState }) => (
            <Field
              label={t('humanAccess.general.name')}
              span={8}
              required
              error={fieldState.error?.message}
            >
              {(props) => (
                <TextInput
                  {...props}
                  {...field}
                  readOnly={!editable}
                  placeholder={t('humanAccess.general.name.placeholder')}
                />
              )}
            </Field>
          )}
        />

        <Controller
          control={control}
          name="ativa"
          render={({ field }) => (
            <div className={s.estadoCampo}>
              <label className={s.toggle}>
                <input
                  type="checkbox"
                  checked={field.value}
                  disabled={!editable}
                  onChange={(e) => field.onChange(e.target.checked)}
                />
                <strong>{t('humanAccess.general.active')}</strong>
                <span className={s.toggleAjuda}>{t('humanAccess.general.active.help')}</span>
              </label>
              <InfoTip
                text={t('humanAccess.general.active.tip')}
                label={t('humanAccess.general.active.tipLabel')}
              />
            </div>
          )}
        />

        <Controller
          control={control}
          name="description"
          render={({ field }) => (
            <Field label={t('humanAccess.general.description')} span={12}>
              {(props) => (
                <TextArea
                  {...props}
                  {...field}
                  rows={2}
                  readOnly={!editable}
                  placeholder={t('humanAccess.general.description.placeholder')}
                />
              )}
            </Field>
          )}
        />

        <Controller
          control={control}
          name="mode"
          render={({ field }) => (
            <fieldset className={s.modoCampo}>
              <legend className={s.legenda}>{t('humanAccess.general.howToWrite')}</legend>
              <div className={s.opcoes}>
                <label className={s.opcao}>
                  <input
                    type="radio"
                    name={field.name}
                    value="interface"
                    checked={field.value === 'interface'}
                    disabled={!editable}
                    onChange={() => field.onChange('interface')}
                  />
                  <strong>{t('humanAccess.general.byInterface')}</strong>
                  <span className={s.opcaoAjuda}>{t('humanAccess.general.byInterface.help')}</span>
                </label>
                <label className={s.opcao}>
                  <input
                    type="radio"
                    name={field.name}
                    value="rego"
                    checked={field.value === 'rego'}
                    disabled={!editable}
                    onChange={() => field.onChange('rego')}
                  />
                  <strong>{t('humanAccess.general.byRego')}</strong>
                  <span className={s.opcaoAjuda}>{t('humanAccess.general.byRego.help')}</span>
                </label>
              </div>
            </fieldset>
          )}
        />
      </FieldGrid>

      <dl className={s.resumo}>
        <dt>{t('humanAccess.general.summary.identities')}</dt>
        <dd>
          {valores.mode === 'rego' ? (
            <span className={s.vazio}>{t('humanAccess.grants.rego')}</span>
          ) : valores.allIdentities ? (
            // «todas» não é "muitas identidades": listar chips aqui esconderia a
            // herança atrás da lista de hoje (P6.2).
            <Chip icon="robot">{t('humanAccess.identities.all')}</Chip>
          ) : selecionadas.length ? (
            <span className={s.chips}>
              {selecionadas.map((identidade) => (
                <Chip key={identidade.identity_id} icon="robot">
                  {identidade.slug}
                </Chip>
              ))}
            </span>
          ) : (
            <span className={s.vazio}>{t('humanAccess.general.summary.noIdentities')}</span>
          )}
        </dd>

        <dt>{t('humanAccess.general.summary.asserts')}</dt>
        <dd>{grantsText({ mode: valores.mode, grants: valores.grants })}</dd>

        <dt>{t('humanAccess.general.summary.conditions')}</dt>
        <dd>
          {valores.mode === 'rego'
            ? t('humanAccess.grants.rego')
            : conditionsText({ claims: valores.claims })}
        </dd>
      </dl>
    </>
  );
}
